import type { StarVariantProps } from "../../utils/star-variants";

import GoldHalfStar from "@homeinstead/icons/GoldHalfStar";
import GoldStar from "@homeinstead/icons/GoldStar";
import GoldStarEmpty from "@homeinstead/icons/GoldStarEmpty";
import HalfStar from "@homeinstead/icons/HalfStar";
import StarSVG from "@homeinstead/icons/Star";
import { cn } from "../../utils/classnames";
import { starVariants } from "../../utils/star-variants";
import { roundToNearestTenth } from "./round-to-nearest-tenth";

type RatingStarsProps = {
  rating: number;
  color?: StarVariantProps["color"];
  ariaLabel?: string;
};

/**
 * RatingStars component to display the stars based on the rating. Each star
 * carries `data-rating-star` (`full`, `half`, or `empty`) for styling hooks.
 */
function RatingStars({ rating, color, ariaLabel }: RatingStarsProps) {
  const isGold = color === "gold";
  const roundedRating = roundToNearestTenth(rating);
  const decimal = roundToNearestTenth(
    roundedRating - Math.floor(roundedRating),
  );
  const fullStars = Math.floor(roundedRating) + (decimal >= 0.8 ? 1 : 0);
  const halfStars = decimal >= 0.3 && decimal < 0.8 ? 1 : 0;
  const emptyStars = 5 - fullStars - halfStars;

  return (
    <div
      aria-label={ariaLabel ?? `Rating: ${rating} out of 5`}
      className={cn("flex flex-row items-center", isGold && "gap-2")}
      role="img"
    >
      {Array.from({ length: fullStars }, (_, i) =>
        isGold ? (
          <GoldStar
            aria-label="Star full"
            data-rating-star="full"
            key={`full-${i}`}
          />
        ) : (
          <StarSVG
            aria-label="Star full"
            className={cn(starVariants({ color, variant: "full" }))}
            data-rating-star="full"
            key={`full-${i}`}
          />
        ),
      )}

      {halfStars === 1 &&
        (isGold ? (
          <GoldHalfStar aria-label="Half-star" data-rating-star="half" />
        ) : (
          <HalfStar
            aria-label="Half-star"
            className={cn(starVariants({ color }))}
            data-rating-star="half"
          />
        ))}

      {Array.from({ length: emptyStars }, (_, i) =>
        isGold ? (
          <GoldStarEmpty
            aria-label="Star empty"
            data-rating-star="empty"
            key={`empty-${i}`}
          />
        ) : (
          <StarSVG
            aria-label="Star empty"
            className={cn(starVariants({ color, variant: "empty" }))}
            data-rating-star="empty"
            key={`empty-${i}`}
          />
        ),
      )}
    </div>
  );
}

export { RatingStars };
